import { useState, useRef, useEffect } from 'react'
import {
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  Mail,
  Webhook,
  FileSpreadsheet,
  Sparkles,
  Copy,
  Check,
  Trash2,
  FileText,
  Star,
  StarOff,
  Code,
  AlertTriangle,
  MoreVertical,
} from 'lucide-react'
import type { ItemDetailProps, SourceType } from './types'

// Item actions dropdown menu
interface ItemActionsDropdownProps {
  onDelete: () => void
}

function ItemActionsDropdown({ onDelete }: ItemActionsDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-500 dark:text-stone-400 hover:bg-stone-50 dark:hover:bg-stone-700 hover:text-stone-700 dark:hover:text-stone-300 transition-colors"
      >
        <MoreVertical className="w-4 h-4" />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-stone-800 rounded-xl shadow-lg border border-stone-200 dark:border-stone-700 py-1 z-50">
          <button
            onClick={() => {
              onDelete()
              setIsOpen(false)
            }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            Delete Item
          </button>
        </div>
      )}
    </div>
  )
}

// Parse sender string to extract name and email
function parseSender(sender: string): { name: string | null; email: string | null; raw: string } {
  if (!sender || !sender.trim()) {
    return { name: null, email: null, raw: sender }
  }

  const trimmed = sender.trim()

  // Pattern 1: "Name <email@example.com>" or "Name" <email@example.com>
  const nameEmailMatch = trimmed.match(/^["']?([^"'<]+?)["']?\s*<([^>]+)>$/)
  if (nameEmailMatch) {
    const name = nameEmailMatch[1].trim()
    const email = nameEmailMatch[2].trim()
    // Validate email has @ sign
    if (email.includes('@')) {
      return { name: name || null, email, raw: trimmed }
    }
  }

  // Pattern 2: <email@example.com> (email only in brackets)
  const bracketEmailMatch = trimmed.match(/^<([^>]+)>$/)
  if (bracketEmailMatch) {
    const email = bracketEmailMatch[1].trim()
    if (email.includes('@')) {
      return { name: null, email, raw: trimmed }
    }
  }

  // Pattern 3: email@example.com (plain email)
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (emailRegex.test(trimmed)) {
    return { name: null, email: trimmed, raw: trimmed }
  }

  // Pattern 4: Contains an email somewhere in the string
  const embeddedEmailMatch = trimmed.match(/([^\s<>]+@[^\s<>]+\.[^\s<>]+)/)
  if (embeddedEmailMatch) {
    const email = embeddedEmailMatch[1]
    // Try to extract name from what's before the email
    const beforeEmail = trimmed.substring(0, trimmed.indexOf(email)).trim()
    const cleanName = beforeEmail.replace(/[<>"']/g, '').trim()
    return { name: cleanName || null, email, raw: trimmed }
  }

  // No email found, return raw content
  return { name: null, email: null, raw: trimmed }
}

export function ItemDetail({
  item,
  onFlagAsGem,
  onUnflagGem,
  onDelete,
  onCopyContent,
  onViewReport,
  onBack,
}: ItemDetailProps) {
  const [copied, setCopied] = useState(false)
  const [copiedSender, setCopiedSender] = useState(false)
  const [showRawJson, setShowRawJson] = useState(false)

  // Parse sender if present
  const parsedSender = item.sender ? parseSender(item.sender) : null

  const handleCopySenderEmail = () => {
    if (parsedSender?.email) {
      navigator.clipboard.writeText(parsedSender.email)
      setCopiedSender(true)
      setTimeout(() => setCopiedSender(false), 2000)
    }
  }

  const handleCopy = () => {
    onCopyContent?.()
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  const formatReportDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const SourceIcon: Record<SourceType, typeof Mail> = {
    email: Mail,
    webhook: Webhook,
    csv: FileSpreadsheet,
  }

  // Helper to format key names for display (e.g., "received_at" -> "Received At")
  const formatKeyName = (key: string): string => {
    return key
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/\b\w/g, (c) => c.toUpperCase())
  }

  // Recursive component to render payload content prettily
  const renderPayloadValue = (value: unknown, depth: number = 0): React.ReactNode => {
    if (value === null || value === undefined) {
      return <span className="text-stone-400 dark:text-stone-500 italic">None</span>
    }

    if (typeof value === 'boolean') {
      return <span>{value ? 'Yes' : 'No'}</span>
    }

    if (typeof value === 'number') {
      return <span>{value}</span>
    }

    if (typeof value === 'string') {
      // Check if it's a date string
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
        try {
          const date = new Date(value)
          return (
            <span>
              {date.toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}{' '}
              at{' '}
              {date.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
              })}
            </span>
          )
        } catch {
          return <span>{value}</span>
        }
      }
      return <span className="whitespace-pre-wrap">{value}</span>
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        return <span className="text-stone-400 dark:text-stone-500 italic">Empty list</span>
      }
      return (
        <ul className="list-disc list-inside space-y-1 mt-1">
          {value.map((item, index) => (
            <li key={index}>{renderPayloadValue(item, depth + 1)}</li>
          ))}
        </ul>
      )
    }

    if (typeof value === 'object') {
      const entries = Object.entries(value as Record<string, unknown>)
      if (entries.length === 0) {
        return <span className="text-stone-400 dark:text-stone-500 italic">Empty</span>
      }
      return (
        <div className={depth > 0 ? 'mt-2 pl-4 border-l-2 border-stone-200 dark:border-stone-700' : ''}>
          {entries.map(([key, val]) => (
            <div key={key} className="mb-4 last:mb-0">
              <div className="font-semibold text-stone-900 dark:text-stone-100">
                {formatKeyName(key)}
              </div>
              <div className="mt-1 text-stone-700 dark:text-stone-300">
                {renderPayloadValue(val, depth + 1)}
              </div>
            </div>
          ))}
        </div>
      )
    }

    return <span>{String(value)}</span>
  }

  // Try to parse and render payload content for webhook/csv sources
  const renderContent = () => {
    const isPayloadSource = item.sourceType === 'webhook' || item.sourceType === 'csv'

    if (!isPayloadSource) {
      return (
        <pre className="text-sm text-stone-700 dark:text-stone-300 leading-relaxed whitespace-pre-wrap font-sans">
          {item.content}
        </pre>
      )
    }

    // Try to parse as JSON
    try {
      const parsed = JSON.parse(item.content)
      if (typeof parsed === 'object' && parsed !== null) {
        return (
          <div className="text-sm leading-relaxed">
            {renderPayloadValue(parsed)}
          </div>
        )
      }
    } catch {
      // Not valid JSON, fall through to default rendering
    }

    // Fallback to plain text display
    return (
      <pre className="text-sm text-stone-700 dark:text-stone-300 leading-relaxed whitespace-pre-wrap font-sans">
        {item.content}
      </pre>
    )
  }

  const Icon = SourceIcon[item.sourceType]

  const sourceLabels: Record<SourceType, string> = {
    email: 'Email',
    webhook: 'Webhook',
    csv: 'CSV Import',
  }

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950">
      {/* Header */}
      <header className="bg-white dark:bg-stone-900">
        <div className="max-w-4xl mx-auto px-6 py-5">
          {/* Breadcrumb */}
          {onBack && (
            <button
              onClick={onBack}
              className="inline-flex items-center gap-1 text-sm text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-300 transition-colors mb-4"
            >
              <ChevronLeft className="w-4 h-4" />
              <span>Items</span>
            </button>
          )}

          {/* Title row with actions */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              {/* Title with status badges */}
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-2xl font-semibold text-stone-900 dark:text-stone-100">
                  {item.subject}
                </h1>
                {item.rejected && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 text-sm font-medium">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    Rejected
                  </span>
                )}
              </div>

              {/* Subtitle: date, stream (linked), source type */}
              <div className="flex items-center gap-2 mt-1.5 text-sm text-stone-500 dark:text-stone-400 flex-wrap">
                <span>{formatDate(item.receivedAt)}</span>
                <span className="text-stone-300 dark:text-stone-600">·</span>
                <button
                  onClick={() => window.location.href = `/streams/${item.streamId}`}
                  className="hover:text-teal-600 dark:hover:text-teal-400 transition-colors"
                >
                  {item.streamName}
                </button>
                <span className="text-stone-300 dark:text-stone-600">·</span>
                <span className="flex items-center gap-1.5">
                  <Icon className="w-4 h-4" />
                  {sourceLabels[item.sourceType]}
                </span>
              </div>
            </div>

            {/* Actions dropdown */}
            <div className="flex items-center gap-3 pt-1">
              <ItemActionsDropdown onDelete={onDelete} />
            </div>
          </div>
        </div>
      </header>

      {/* Consolidated Actions Bar */}
      <div className="bg-white dark:bg-stone-900 border-t border-b border-stone-200 dark:border-stone-800">
        <div className="max-w-4xl mx-auto px-6 py-3">
          <div className="flex flex-wrap items-center gap-2">
            {item.isGem ? (
              <button
                onClick={onUnflagGem}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-amber-700 dark:text-amber-400 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-600 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors"
              >
                <StarOff className="w-4 h-4" />
                <span>Unflag Gem</span>
              </button>
            ) : (
              <button
                onClick={onFlagAsGem}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-stone-700 dark:text-stone-300 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-600 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-900/20 hover:text-amber-700 dark:hover:text-amber-400 transition-colors"
              >
                <Star className="w-4 h-4" />
                <span>Flag as Gem</span>
              </button>
            )}
            <button
              onClick={handleCopy}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-stone-700 dark:text-stone-300 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-600 rounded-lg hover:bg-stone-50 dark:hover:bg-stone-700 transition-colors"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                  <span className="text-green-600 dark:text-green-400">Copied</span>
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  <span>Copy Content</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {/* Gem Reason (if applicable) - shown first */}
        {item.isGem && item.gemReason && (
          <section className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-5">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-amber-900 dark:text-amber-200 mb-1">
                  Why this is a gem
                </h3>
                <p className="text-sm text-amber-800 dark:text-amber-300">
                  {item.gemReason}
                </p>
              </div>
            </div>
          </section>
        )}

        {/* Sender Card (if present) */}
        {parsedSender && (parsedSender.name || parsedSender.email) && (
          <section>
            <h2 className="text-xs font-medium text-stone-400 dark:text-stone-500 uppercase tracking-wide mb-3">
              From
            </h2>
            <div className="rounded-xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900/50 p-5">
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  {parsedSender.name && (
                    <p className="font-medium text-stone-900 dark:text-stone-100">
                      {parsedSender.name}
                    </p>
                  )}
                  {parsedSender.email && (
                    <p className={`text-stone-500 dark:text-stone-400 ${parsedSender.name ? 'text-sm' : ''}`}>
                      {parsedSender.email}
                    </p>
                  )}
                </div>
                {parsedSender.email && (
                  <button
                    onClick={handleCopySenderEmail}
                    className="flex-shrink-0 inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-stone-700 dark:text-stone-300 bg-stone-100 dark:bg-stone-800 rounded-lg hover:bg-stone-200 dark:hover:bg-stone-700 transition-colors"
                  >
                    {copiedSender ? (
                      <>
                        <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                        <span className="text-green-600 dark:text-green-400">Copied</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        <span>Copy Email</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </section>
        )}

        {/* Rejection Warning (if applicable) */}
        {item.rejected && (
          <section className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-5">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-red-900 dark:text-red-200 mb-1">
                  Item Rejected
                </h3>
                <p className="text-sm text-red-800 dark:text-red-300">
                  {item.rejectedReason || 'This item was rejected during processing and will not be included in reports.'}
                </p>
              </div>
            </div>
          </section>
        )}

        {/* Content */}
        <section>
          <h2 className="text-xs font-medium text-stone-400 dark:text-stone-500 uppercase tracking-wide mb-3">
            Content
          </h2>
          <div className="rounded-xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900/50 p-6">
            {renderContent()}
          </div>

          {/* Raw JSON toggle for webhook/csv sources */}
          {(item.sourceType === 'webhook' || item.sourceType === 'csv') && (
            <div className="mt-3">
              <button
                onClick={() => setShowRawJson(!showRawJson)}
                className="inline-flex items-center gap-1.5 text-xs text-stone-400 dark:text-stone-500 hover:text-stone-600 dark:hover:text-stone-400 transition-colors"
              >
                {showRawJson ? (
                  <ChevronDown className="w-3.5 h-3.5" />
                ) : (
                  <ChevronRight className="w-3.5 h-3.5" />
                )}
                <Code className="w-3.5 h-3.5" />
                <span>View raw data</span>
              </button>
              {showRawJson && (
                <div className="mt-2 rounded-xl border border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-900 p-5 overflow-x-auto">
                  <pre className="text-xs text-stone-600 dark:text-stone-400 font-mono whitespace-pre-wrap">
                    {(() => {
                      try {
                        return JSON.stringify(JSON.parse(item.content), null, 2)
                      } catch {
                        return item.content
                      }
                    })()}
                  </pre>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Related Reports */}
        {item.relatedReports.length > 0 && (
          <section>
            <h2 className="text-xs font-medium text-stone-400 dark:text-stone-500 uppercase tracking-wide mb-3">
              Cited In Reports
            </h2>
            <div className="space-y-2">
              {item.relatedReports.map((report) => (
                <button
                  key={report.id}
                  onClick={() => onViewReport?.(report.id)}
                  className="w-full text-left group flex items-center gap-3 p-4 rounded-xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900/50 hover:border-teal-300 dark:hover:border-teal-700 transition-colors"
                >
                  <div className="w-8 h-8 rounded-lg bg-stone-100 dark:bg-stone-800 flex items-center justify-center group-hover:bg-teal-100 dark:group-hover:bg-teal-900/40 transition-colors">
                    <FileText className="w-4 h-4 text-stone-500 dark:text-stone-400 group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-stone-900 dark:text-stone-100 truncate group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors">
                      {report.title}
                    </p>
                    <p className="text-xs text-stone-500 dark:text-stone-400">
                      {formatReportDate(report.date)}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* No Related Reports */}
        {item.relatedReports.length === 0 && (
          <section>
            <h2 className="text-xs font-medium text-stone-400 dark:text-stone-500 uppercase tracking-wide mb-3">
              Cited In Reports
            </h2>
            <div className="rounded-xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900/50 p-8 text-center">
              <p className="text-sm text-stone-500 dark:text-stone-400">
                This item hasn't been cited in any reports yet.
              </p>
            </div>
          </section>
        )}
      </main>
    </div>
  )
}
